import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, imgUrl, errMsg, fmtDate, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Spinner } from "@/components/RequireAuth";
import GuestCard from "@/components/GuestCard";
import BlockDatesDialog from "@/components/BlockDatesDialog";
import StatusBadge, { bookingState } from "@/components/StatusBadge";
import { ReviewDialog } from "@/components/Dialogs";
import { Rating } from "@/components/Reviews";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Home, Inbox, ShieldCheck, Eye, Scale, Landmark } from "lucide-react";
import { PayoutSummary } from "@/components/PayoutSummary";
import { HostTaxReminder } from "@/components/AdminIncome";
import { HostLivePayoutPrompt } from "@/components/HostLivePayout";
import { TaxReport } from "@/components/TaxReport";
import { MessagesButton } from "@/components/BookingMessages";
import { WelcomeGuide } from "@/components/WelcomeGuide";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { POLICY_SUMMARY } from "@/pages/NonDiscrimination";
import { LEGAL } from "@/pages/Legal";

function HostingSwitch() {
  const { user, setUser } = useAuth();
  const [agree, setAgree] = useState(false);
  const toggle = async (v) => {
    try {
      const { data } = await api.patch("/users/me", { is_host: v, agree_host_agreement: agree });
      setUser(data);
      toast.success(v ? "Hosting is on. Create your first listing." : "Hosting turned off");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="mx-auto mt-16 max-w-xl rounded-3xl border bg-white p-10 text-center" data-testid="hosting-off-card">
      <Home className="mx-auto mb-4 h-9 w-9 text-primary" />
      <h1 className="mb-2 text-3xl font-bold">Turn on hosting</h1>
      <p className="mb-6 text-stone-600">List your place for free. When you're booked, we keep a flat 10% and guests pay no service fee.</p>
      <ul className="mb-8 space-y-3 rounded-2xl bg-teal-50 p-5 text-left text-sm text-teal-900" data-testid="host-protection-promise">
        <li className="flex gap-2"><ShieldCheck className="h-5 w-5 shrink-0 text-primary" /><span><b>We back hosts.</b> If a guest complains, we side with you unless their evidence clearly supports the complaint.</span></li>
        <li className="flex gap-2"><Eye className="h-5 w-5 shrink-0 text-primary" /><span><b>See who's coming.</b> Every request shows the guest's past stays, host reviews and complaint history before you accept.</span></li>
      </ul>
      <label className="mb-6 flex items-start gap-3 rounded-2xl border p-4 text-left text-sm text-stone-700" data-testid="nondiscrimination-agreement">
        <Checkbox data-testid="nondiscrimination-checkbox" checked={agree} onCheckedChange={(v) => setAgree(!!v)} className="mt-0.5" />
        <span>I have read and agree to the <Link to="/host-agreement" className="font-semibold text-primary hover:underline" data-testid="hosting-agreement-link">Host Agreement</Link>, including the required insurance, permit rules and the <Link to="/non-discrimination" className="font-semibold text-primary hover:underline">non-discrimination policy</Link>. {POLICY_SUMMARY}</span>
      </label>
      <label className="inline-flex items-center gap-3 font-medium">
        <Switch data-testid="hosting-switch" disabled={!agree} checked={!!user.is_host} onCheckedChange={toggle} /> I want to host
      </label>
    </div>
  );
}

const Empty = ({ text }) => (
  <div className="rounded-3xl border bg-white py-16 text-center text-stone-500"><Inbox className="mx-auto mb-2 h-8 w-8" />{text}</div>
);

function DeclineDialog({ b, reload }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const { data: reasons = [] } = useQuery({ queryKey: ["decline-reasons"], queryFn: () => api.get("/decline-reasons").then((r) => r.data) });
  const decline = async () => {
    try {
      await api.post(`/bookings/${b.booking_id}/decline`, { reason });
      toast.success("Request declined");
      setOpen(false);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid={`host-decline-${b.booking_id}`} variant="outline" className="rounded-full">Decline</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Why are you declining?</DialogTitle>
          <DialogDescription>{POLICY_SUMMARY}</DialogDescription>
        </DialogHeader>
        <Select value={reason} onValueChange={setReason}>
          <SelectTrigger data-testid="decline-reason-select"><SelectValue placeholder="Choose a reason" /></SelectTrigger>
          <SelectContent>{reasons.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
        </Select>
        <Button data-testid="decline-confirm-button" disabled={!reason} onClick={decline} className="rounded-full">Decline request</Button>
      </DialogContent>
    </Dialog>
  );
}

function BookingRow({ b, reload, actions }) {
  const act = async (verb) => {
    try {
      await api.post(`/bookings/${b.booking_id}/${verb}`);
      toast.success("Accepted. The guest can now pay.");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="grid gap-5 rounded-3xl border bg-white p-5 md:grid-cols-[1fr_340px]" data-testid={`host-booking-${b.booking_id}`}>
      <div className="space-y-2">
        <StatusBadge status={bookingState(b)} />
        <p className="text-lg font-semibold">{b.listing.title}</p>
        <p className="text-sm text-stone-600">{fmtDate(b.check_in)} → {fmtDate(b.check_out)} · {b.nights} nights · {b.guests} guest{b.guests === 1 ? "" : "s"}</p>
        <p className="text-sm">Guest pays <span className="font-mono-num">{money(b.total)}</span>{b.tax_total > 0 && <> (incl. <span className="font-mono-num">{money(b.tax_total)}</span> lodging tax you remit)</>} · commission <span className="font-mono-num">{money(b.commission)}</span> · <span className="font-semibold">you get <span className="font-mono-num">{money(b.host_net)}</span>{b.tax_total > 0 && " + tax"}</span></p>
        {b.message && <p className="rounded-2xl bg-stone-50 p-3 text-sm italic text-stone-700">“{b.message}”</p>}
        <div className="flex flex-wrap gap-2 pt-2">
          <MessagesButton bookingId={b.booking_id} />
          {actions && b.status === "requested" && <>
            <Button data-testid={`host-accept-${b.booking_id}`} className="rounded-full" onClick={() => act("accept")}>Accept</Button>
            <DeclineDialog b={b} reload={reload} />
          </>}
          {b.completed && !b.has_reviewed && <ReviewDialog booking={b} who="this guest" onDone={reload} />}
          {b.complaint && <Button asChild size="sm" variant="outline" className="rounded-full"><Link to={`/complaints/${b.complaint.complaint_id}`}>Open complaint</Link></Button>}
        </div>
      </div>
      <GuestCard g={b.guest} />
    </div>
  );
}

function PolicyGate() {
  const { setUser } = useAuth();
  const [agree, setAgree] = useState(false);
  const save = async () => {
    try {
      setUser((await api.patch("/users/me", { agree_host_agreement: true })).data);
      toast.success("Thanks. You can now accept and decline requests.");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="mb-8 space-y-3 rounded-3xl border border-amber-200 bg-amber-50 p-5 text-sm" data-testid="policy-gate">
      <p className="font-semibold">Please accept the Host Agreement to keep responding to requests</p>
      <label className="flex items-start gap-3 text-stone-700">
        <Checkbox data-testid="policy-gate-checkbox" checked={agree} onCheckedChange={(v) => setAgree(!!v)} className="mt-0.5" />
        <span>I have read and agree to the <Link to="/host-agreement" className="font-semibold text-primary hover:underline">Host Agreement</Link>, including the insurance requirement and the <Link to="/non-discrimination" className="font-semibold text-primary hover:underline">non-discrimination policy</Link>.</span>
      </label>
      <Button size="sm" className="rounded-full" disabled={!agree} onClick={save} data-testid="policy-gate-agree">I agree</Button>
    </div>
  );
}

const Metric = ({ label, value, accent, testid }) => (
  <div className={`rounded-3xl border p-6 ${accent || "bg-white"}`}>
    <p className="text-xs font-semibold uppercase tracking-wider text-stone-500">{label}</p>
    <p className="mt-2 font-mono-num text-3xl font-semibold" data-testid={testid}>{value}</p>
  </div>
);

const todayISO = () => new Date().toISOString().slice(0, 10);
const insuranceLapsed = (l) => !l.is_sample && (l.insurance_expiry || "") <= todayISO();

function liveLabel(l) {
  if (l.approval_status !== "approved") return "Not live yet";
  if (insuranceLapsed(l) || !l.active) return "Hidden";
  return "Live";
}

function HostListingCard({ l, reload }) {
  return (
    <div className="group overflow-hidden rounded-3xl border bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-xl" data-testid={`host-listing-${l.listing_id}`}>
      <img src={imgUrl(l.photos?.[0])} alt="" className="aspect-[4/3] w-full bg-stone-100 object-cover" />
      <div className="space-y-2 p-5">
        <div className="flex items-center justify-between gap-2"><p className="truncate font-semibold">{l.title}</p><Rating value={l.rating} count={l.review_count} /></div>
        <div className="flex flex-wrap gap-2">
          <StatusBadge status={l.approval_status} testid={`host-listing-approval-${l.listing_id}`} />
          {insuranceLapsed(l) && <span className="rounded-full border border-red-200 bg-red-50 px-2.5 py-0.5 text-xs font-semibold text-red-800" data-testid={`host-listing-insurance-expired-${l.listing_id}`}>Insurance missing or expired</span>}
        </div>
        {l.approval_status === "rejected" && l.approval_note && <p className="text-xs text-red-700">{l.approval_note}</p>}
        <p className="text-sm text-stone-500">{money(l.price_per_night)} / night · {liveLabel(l)} · {l.blocked_dates?.length || 0} blocked</p>
        <div className="flex gap-2 pt-1">
          <Button asChild size="sm" variant="outline" className="rounded-full" data-testid={`host-edit-${l.listing_id}`}><Link to={`/host/listings/${l.listing_id}/edit`}>Edit</Link></Button>
          <BlockDatesDialog key={l.blocked_dates?.join()} listing={l} onDone={reload} />
        </div>
      </div>
    </div>
  );
}

function EarningsPanel({ e }) {
  const { data: s } = useQuery({ queryKey: ["public-settings"], queryFn: fetcher("/settings/public") });
  return (
    <>
      {s && !s.payments_live && <p className="mb-4 inline-flex rounded-full bg-amber-100 px-3 py-1 text-xs font-bold uppercase tracking-wider text-amber-900" data-testid="earnings-test-mode-badge">Test mode: these totals are not real money</p>}
      <div className={`mb-10 grid gap-4 ${e.tax_collected > 0 ? "md:grid-cols-2 lg:grid-cols-4" : "md:grid-cols-3"}`} data-testid="host-earnings">
        <Metric label="Gross bookings (before tax)" value={money(e.gross)} testid="earnings-gross" />
        <Metric label="FairStay commission (10%)" value={money(e.commission)} testid="earnings-commission" />
        <Metric label="Your earnings after FairStay's 10%" value={money(e.net_owed)} accent="bg-emerald-50 border-emerald-200" testid="earnings-net" />
        {e.tax_collected > 0 && <Metric label="Lodging tax collected (you remit)" value={money(e.tax_collected)} testid="earnings-tax-collected" />}
      </div>
      <HostLivePayoutPrompt />
      <HostTaxReminder />
      <PayoutSummary />
      <TaxReport />
      {e.gross > 0 && (
        <p className="-mt-6 mb-10 text-sm text-stone-600" data-testid="earnings-airbnb-compare">
          Estimates for the same stays: on Airbnb's 15.5% host-only fee you'd have paid about <span className="font-semibold">{money(e.commission * 1.55)}</span>; on its older split fee, guests would have paid about <span className="font-semibold">{money(e.airbnb_guest_fee)}</span> extra and total fees would be around <span className="font-semibold">{money(e.airbnb_total_fees)}</span>. On Vrbo, you'd have paid about <span className="font-semibold">{money(e.vrbo_host_fee)}</span> in host fees, with total fees around <span className="font-semibold">{money(e.vrbo_total_fees)}</span>. With us, it's just <span className="font-semibold">{money(e.commission)}</span>. Real fees vary by account.
          {e.refunds > 0 && <> Refunds issued: {money(e.refunds)}.</>} Guest payments go straight to your Stripe account with the 10% already taken out, and Stripe pays you on your payout schedule. Stripe's card processing fee also comes out of your Stripe balance and isn't returned when a guest is refunded.
        </p>
      )}
    </>
  );
}

function ComplaintsTab({ items }) {
  return (
    <>
      <p className="flex items-center gap-2 rounded-2xl bg-teal-50 px-4 py-3 text-sm text-teal-900" data-testid="host-complaints-protection-note">
        <ShieldCheck className="h-4 w-4 shrink-0 text-primary" />We back you unless the guest's evidence clearly supports the complaint. Reply with your own photos to make your case.
      </p>
      {items.length ? items.map((c) => (
        <Link key={c.complaint_id} to={`/complaints/${c.complaint_id}`} className="flex flex-wrap items-center gap-3 rounded-2xl border bg-white p-4 hover:shadow-md" data-testid={`host-complaint-${c.complaint_id}`}>
          <StatusBadge status={c.decision || c.status} />
          <span className="font-semibold">{c.category}</span>
          <span className="text-sm text-stone-500">{c.listing?.title} · from {c.guest?.name} · {fmtDate(c.created_at)}</span>
          {c.status === "open" && <span className="ml-auto text-sm font-semibold text-secondary">Reply with your evidence →</span>}
        </Link>
      )) : <Empty text="No complaints. Nice work." />}
    </>
  );
}

const fetcher = (url) => () => api.get(url).then((r) => r.data);

function useHostData(user) {
  const qc = useQueryClient();
  const enabled = !!user.is_host;
  const bookings = useQuery({ queryKey: ["host-bookings"], queryFn: fetcher("/host/bookings"), enabled });
  const listings = useQuery({ queryKey: ["host-listings"], queryFn: fetcher("/host/listings"), enabled });
  const earnings = useQuery({ queryKey: ["host-earnings"], queryFn: fetcher("/host/earnings"), enabled });
  const complaints = useQuery({ queryKey: ["my-complaints"], queryFn: fetcher("/complaints/mine"), enabled });
  const all = bookings.data || [];
  const hostComplaints = (complaints.data || []).filter((c) => c.host_id === user.user_id);
  return {
    enabled,
    loading: bookings.isLoading || listings.isLoading,
    reload: () => ["host-bookings", "host-listings", "host-earnings"].forEach((k) => qc.invalidateQueries({ queryKey: [k] })),
    requests: all.filter((b) => b.status === "requested"),
    stays: all.filter((b) => ["accepted", "confirmed"].includes(b.status)),
    listings: listings.data || [],
    earnings: earnings.data || {},
    complaints: hostComplaints,
    openComplaints: hostComplaints.filter((c) => c.status === "open").length,
  };
}

export default function HostDashboard() {
  const { user } = useAuth();
  const d = useHostData(user);
  if (!d.enabled) return <HostingSwitch />;
  if (d.loading) return <Spinner />;
  const agreed = user.host_agreement_version === LEGAL.version;

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <h1 className="text-4xl font-bold tracking-tight">Hosting</h1>
        <div className="flex flex-wrap gap-3">
          <Button asChild variant="outline" className="rounded-full" data-testid="host-payouts-link"><Link to="/host/payouts"><Landmark className="mr-1 h-4 w-4" />Payouts &amp; Store</Link></Button>
          <Button asChild className="rounded-full" data-testid="host-new-listing-button"><Link to="/host/listings/new"><Plus className="mr-1 h-4 w-4" />New listing</Link></Button>
        </div>
      </div>
      {!agreed && <PolicyGate />}
      <WelcomeGuide />
      <EarningsPanel e={d.earnings} />
      <Tabs defaultValue="requests">
        <TabsList className="mb-6 flex-wrap rounded-full">
          <TabsTrigger value="requests" className="rounded-full" data-testid="host-tab-requests">Requests ({d.requests.length})</TabsTrigger>
          <TabsTrigger value="stays" className="rounded-full" data-testid="host-tab-stays">Stays ({d.stays.length})</TabsTrigger>
          <TabsTrigger value="listings" className="rounded-full" data-testid="host-tab-listings">Listings ({d.listings.length})</TabsTrigger>
          <TabsTrigger value="complaints" className="rounded-full" data-testid="host-tab-complaints">Complaints{d.openComplaints ? ` (${d.openComplaints} new)` : ""}</TabsTrigger>
        </TabsList>
        <TabsContent value="requests" className="space-y-4">
          <p className="flex items-start gap-2 rounded-2xl border bg-white px-4 py-3 text-sm text-stone-700" data-testid="requests-nondiscrimination-note">
            <Scale className="mt-0.5 h-4 w-4 shrink-0 text-primary" /><span>{POLICY_SUMMARY} <Link to="/non-discrimination" className="font-semibold text-primary hover:underline">Read the policy</Link></span>
          </p>
          {d.requests.length ? d.requests.map((b) => <BookingRow key={b.booking_id} b={b} reload={d.reload} actions={agreed} />) : <Empty text="No pending requests" />}
        </TabsContent>
        <TabsContent value="stays" className="space-y-4">
          {d.stays.length ? d.stays.map((b) => <BookingRow key={b.booking_id} b={b} reload={d.reload} />) : <Empty text="No upcoming or past stays yet" />}
        </TabsContent>
        <TabsContent value="listings">
          {d.listings.length ? (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{d.listings.map((l) => <HostListingCard key={l.listing_id} l={l} reload={d.reload} />)}</div>
          ) : <Empty text="You haven't listed a place yet" />}
        </TabsContent>
        <TabsContent value="complaints" className="space-y-3"><ComplaintsTab items={d.complaints} /></TabsContent>
      </Tabs>
    </div>
  );
}
